import React, { useContext, useState, useRef } from 'react'

import ConfirmationDialog from 'components/ConfirmationDialog'
import { stripMarkdownParser } from 'components/Markdown/parsers'
import ModalContext from 'containers/ModalManager/ModalContext'

import { useActivityComments } from './ActivityCommentsContext'
import { useCommentDelete } from '../operations'

import { useUpdateCommentMutation, CommentInfoFragment } from 'gqlTypes'
import Comment, { RepliesMeta, OnEditProps } from 'components/Comment'

interface ActivityCommentProps {
  parentCommentIdentifier?: string
  Comment: typeof Comment
  repliesMeta?: RepliesMeta
  permanentArtboardShortId: string
  documentVersionShortId: string
  shareIdentifier: string
  identifier: string
  isRead: boolean
  isEdited: boolean
  body: string
  user: CommentInfoFragment['user']
  createdAt: string
  updatedAt: string
  userIsCreator: boolean
  userCanDelete: boolean
}

export const ActivityComment: React.FC<ActivityCommentProps> = ({
  isEdited,
  isRead,
  user,
  createdAt,
  userIsCreator,
  userCanDelete,
  updatedAt,
  body,
  permanentArtboardShortId,
  documentVersionShortId,
  shareIdentifier,
  identifier,
  Comment,
  repliesMeta,
  parentCommentIdentifier,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const { showModal, hideModal } = useContext(ModalContext)

  // Operations
  const [updateComment] = useUpdateCommentMutation({
    redirectErrors: false,
    onError: 'unsafe-throw-exception',
  })
  const [deleteComment] = useCommentDelete({
    permanentArtboardShortId,
    documentVersionShortId,
    parentCommentIdentifier,
  })
  const {
    activeComment,
    setActiveComment,
    updateValue,
    resetActiveComment,
  } = useActivityComments()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    updateValue(event.target.value)

  const handleEdit = ({ toggleEditComment, cancel }: OnEditProps) => {
    if (activeComment.isDirty) {
      const parsedComment = stripMarkdownParser(activeComment.currentValue)

      showModal(ConfirmationDialog, {
        title: 'Discard changes?',
        children: (
          <p>
            You have the following unsaved changes on another comment: &quot;
            {parsedComment.trim()}&quot;
          </p>
        ),
        confirmButton: {
          text: 'Discard Changes',
        },
        cancelButton: {
          text: 'Continue Editing',
        },
        onConfirm: async () => {
          toggleEditComment()

          activeComment.cancel?.()
          setActiveComment({ inputRef, cancel, initialValue: body! })
          hideModal()
        },
        onHide: () => {
          hideModal()
          activeComment.inputRef?.current?.focus()
        },
      })
    } else {
      toggleEditComment()

      activeComment.cancel && activeComment.cancel()
      setActiveComment({ inputRef, cancel, initialValue: body! })
    }
  }

  const handleCancel = () => resetActiveComment()

  const handleDeleteComment = () => {
    setIsDeleting(true)

    showModal(ConfirmationDialog, {
      title: 'Delete comment?',
      children: null,
      confirmButton: {
        text: 'Delete',
      },
      onHide: () => setIsDeleting(false),
      onConfirm: async () => {
        await deleteComment({ variables: { identifier } })
        setIsDeleting(false)
        hideModal()
      },
    })
  }

  const handleUpdateComment = (body: string) => {
    return updateComment({
      variables: {
        identifier,
        input: { body },
      },
    })
  }

  return (
    <Comment
      identifier={identifier}
      inputRef={inputRef}
      className="activity-comment"
      isEdited={isEdited}
      isRead={isRead}
      meta={{
        author: user!.name!,
        avatar: user!.avatar!.small!,
        date: createdAt,
      }}
      userIsCreator={userIsCreator}
      userCanDelete={userCanDelete}
      isDeleting={isDeleting}
      permanentArtboardShortId={permanentArtboardShortId}
      shareIdentifier={shareIdentifier}
      repliesMeta={repliesMeta}
      deleteComment={handleDeleteComment}
      updateComment={handleUpdateComment}
      updatedAt={updatedAt}
      onChange={handleChange}
      onEdit={handleEdit}
      onCancel={handleCancel}>
      {body}
    </Comment>
  )
}

export default ActivityComment
